/* External */
import { all, call, put, select, takeLatest } from "redux-saga/effects";
import cloneDeepL from "lodash/cloneDeep";

/* Actions */
import { actionTypes, contentChanged, contentLoaded } from "./actions";

/* Sagas */
import { getContent } from "../player/content/sagas";

/* Services */
import { backendService } from "../../services";
import { getChromecastSavedData, removeChromecastSavedData, setChromecastSavedData } from "../localStorage";

function* loadMedia({ payload }) {
  const { assetId: stationId, fromPlayer, isLive, playSource, progress } = payload;
  const user = yield select((state) => state.user);
  let asset, content, station;

  if (isLive) {
    const stations = yield select((state) => state.allStations.data);

    station = stations.find(({ id }) => id === stationId);
  }

  if (fromPlayer) {
    asset = yield select((state) => state.player.asset.data);
    content = yield select((state) => state.player.content.data);
  } else {
    try {
      content = yield getContent(payload);
    } catch (error) {
      console.error(`chromecastSaga-loadMedia: ${error}`);
    }
  }

  if (isLive) {
    content = content[0];
  } else {
    content = cloneDeepL(content);

    if (content.season) {
      content.seasonNumber = parseInt(content.season);
      delete content.season;
    }

    if (content.episode) {
      content.episodeNumber = parseInt(content.episode);
      delete content.episode;
    }
  }

  yield put(contentChanged(content));

  if (!asset) {
    let assetId, assetResponse;

    if (isLive) assetId = station?.assetID;
    else assetId = content?.assetIDs?.[0];

    try {
      assetResponse = yield call(backendService.content.getAssetById, assetId);
    } catch (error) {
      console.error(`chromecastSaga-loadMedia: ${error}`);
    }

    asset = assetResponse.data;
  }

  const { duration, liveURLs, vodURLs } = asset;
  const playbackUrl = isLive ? liveURLs.dash.primary : vodURLs.dash.primary;

  const mediaConfig = {
    contentId: playbackUrl,
    customData: {
      asset: {
        ...content,
        isLive,
        playbackURL: playbackUrl
      },
      playSource,
      stationName: station?.name,
      user
    },
    duration: isLive ? null : duration,
    streamType: isLive ? window.chrome.cast.media.StreamType.LIVE : window.chrome.cast.media.StreamType.BUFFERED
  };

  const currentSession = window.cast.framework.CastContext.getInstance().getCurrentSession();
  const loadRequest = new window.chrome.cast.media.LoadRequest(mediaConfig);

  if (progress) loadRequest.currentTime = progress;

  try {
    yield currentSession.loadMedia(loadRequest);
    yield put(contentLoaded());
  } catch (error) {
    yield put(contentChanged(null));
    console.error(`chromecastSaga-loadMedia: ${error}`);
  }
}

function* pageUnloaded({ payload: remotePlayer }) {
  const connectionState = yield select((state) => state.chromecast.connectionState);
  const content = yield select((state) => state.chromecast.content);
  const isConnected = connectionState === window.cast.framework.CastState.CONNECTED;

  if (!isConnected || !content) return;

  const {
    currentTime,
    mediaInfo: { contentId, customData, duration, streamType }
  } = remotePlayer;

  setChromecastSavedData({ contentId, currentTime, customData, duration, streamType });
}

function* reloadSession() {
  const connectionState = yield select((state) => state.chromecast.connectionState);
  const isConnected = connectionState === window.cast.framework.CastState.CONNECTED;
  const savedData = getChromecastSavedData();
  const wasPageReloaded = window.performance.getEntriesByType("navigation")[0].type === "reload";

  if (!savedData) return;
  if (!isConnected || !wasPageReloaded) return removeChromecastSavedData();

  const { currentTime, ...mediaInfo } = savedData;
  const currentSession = window.cast.framework.CastContext.getInstance().getCurrentSession();
  const loadRequest = new window.chrome.cast.media.LoadRequest(mediaInfo);

  if (currentTime) loadRequest.currentTime = currentTime;

  yield currentSession.loadMedia(loadRequest);
}

export default function* () {
  yield all([
    takeLatest(actionTypes.CONNECTION_STATE_CHANGED, reloadSession),
    takeLatest(actionTypes.LOAD_MEDIA, loadMedia),
    takeLatest(actionTypes.PAGE_UNLOADED, pageUnloaded)
  ]);
}
